import { useMemo } from 'react';
import { getQuarterFromMonth, createQuarterlyData } from './useQuarterHelpers';

/**
 * Calcul de la refacturation par consultant
 * Prend en compte le champ tmpexcl pour les temps
 *
 * Règles:
 * - TMP = CA des temps SANS tmpexcl / Jours des temps SANS tmpexcl
 * - Refacturation = (jours normaux × TMP) + CA des temps AVEC tmpexcl
 */
export function useRefacturationCalculations(tempsData) {
  return useMemo(() => {
    const result = {};

    // Grouper les temps par consultant et trimestre
    const tempsByConsultantQuarter = {};

    tempsData.forEach(temps => {
      const consultant = temps.consultantNom;
      if (!consultant) return;

      const month = temps.fields?.Mois;
      if (!month) return;

      const quarter = getQuarterFromMonth(month);
      const key = `${consultant}-${quarter}`;

      if (!tempsByConsultantQuarter[key]) {
        tempsByConsultantQuarter[key] = {
          consultant,
          quarter,
          joursNormaux: 0,
          caNormaux: 0,
          joursTmpExcl: 0,
          caTmpExcl: 0
        };
      }

      const jours = temps.fields?.['Nombre de jours'] || 0;
      const taux = temps.fields?.['Taux journalier (€)'] || 0;
      const ca = jours * taux;
      // Vérification robuste: accepte true, "true", 1, ou toute valeur truthy
      const isTmpExcl = Boolean(temps.fields?.tmpexcl);

      if (isTmpExcl) {
        tempsByConsultantQuarter[key].joursTmpExcl += jours;
        tempsByConsultantQuarter[key].caTmpExcl += ca;
      } else {
        tempsByConsultantQuarter[key].joursNormaux += jours;
        tempsByConsultantQuarter[key].caNormaux += ca;
      }
    });

    // Calculer TMP par trimestre (sans tmpexcl)
    const tmpByQuarter = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
    const joursByQuarter = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
    const caByQuarter = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };

    Object.values(tempsByConsultantQuarter).forEach(data => {
      joursByQuarter[data.quarter] += data.joursNormaux;
      caByQuarter[data.quarter] += data.caNormaux;
    });

    ['Q1', 'Q2', 'Q3', 'Q4'].forEach(q => {
      tmpByQuarter[q] = joursByQuarter[q] > 0
        ? caByQuarter[q] / joursByQuarter[q]
        : 0;
    });

    // Calculer refacturation par consultant
    const consultants = [...new Set(tempsData.map(t => t.consultantNom).filter(Boolean))];

    consultants.forEach(consultant => {
      result[consultant] = createQuarterlyData();

      ['Q1', 'Q2', 'Q3', 'Q4'].forEach(quarter => {
        const key = `${consultant}-${quarter}`;
        const data = tempsByConsultantQuarter[key];

        if (data) {
          const taux = tmpByQuarter[quarter];

          // Refacturation = (jours normaux × TMP) + CA des temps tmpexcl
          const refacNormale = data.joursNormaux * taux;
          const refacTmpExcl = data.caTmpExcl;

          result[consultant][quarter] = refacNormale + refacTmpExcl;
        }
      });

      // Calculer annuel
      result[consultant].ANNUEL =
        result[consultant].Q1 +
        result[consultant].Q2 +
        result[consultant].Q3 +
        result[consultant].Q4;
    });

    return {
      refacturationByConsultant: result,
      tmpByQuarter,
      consultants
    };
  }, [tempsData]);
}
